import {Animated, Dimensions, Image, ImageBackground, Platform, Pressable, StyleSheet, useWindowDimensions} from "react-native"
import {memo, PropTypes, propTypesExact, shapeComponent, ShapeComponent, Text, useBreakpoint, View} from "shared/base"
import React, {useEffect, useMemo} from "react"
import useShowMenu, {breakpointsWithExtraMenuSize, breakpointsWithoutExtraMenuSize} from "./use-show-menu"
import ApiMakerConfig from "@kaspernj/api-maker/build/config"
import background from "./background.png"
import ClickOutsideView from "outside-eye/build/outside-view"
import CommandsPool from "@kaspernj/api-maker/build/commands-pool"
import {digg} from "diggerize"
import {FrontLayoutContext} from "components/front-layout"
import I18n from "shared/i18n"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import isExpo from "@kaspernj/api-maker/src/is-expo"
import isUserA from "components/users/is-a"
import Link from "@kaspernj/api-maker/build/link"
import LogoImage from "./logo.svg"
import Menu from "./menu"
import ProTopBar from "components/user/pro/top-bar"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import Routes from "shared/routes"
import {SchoolClass} from "models"
import setLayout from "shared/set-layout"
import SignIn from "./sign-in"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useEventListener from "@kaspernj/api-maker/build/use-event-listener"
import WeekBar from "components/user/dashboard/week-bar"
import {WithDefaultStyle} from "@kaspernj/api-maker/build/utils/default-style"

const styles = StyleSheet.create({
  defaultText: {
    fontFamily: "HankenGrotesk",
    lineHeight: "170%"
  },
  imageBackground: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: "100%",
    minHeight: "100vh"
  }
})

const defaultStyle = {
  Text: [styles.defaultText]
}

export default memo(shapeComponent(class ChalkyLayout extends ShapeComponent {
  static defaultProps = {
    active: null,
    centered: true,
    children: null,
    dataSet: null,
    layout: "user",
    maxWidth: null,
    menu: true,
    redirectAfterSignIn: false,
    requireUser: true,
    schoolClass: null,
    proTrialTopBar: null,
    userMenu: true,
    weekBar: true
  }

  static propTypes = propTypesExact({
    active: PropTypes.string,
    centered: PropTypes.bool.isRequired,
    children: PropTypes.node,
    dataSet: PropTypes.object,
    headerTitle: PropTypes.string,
    layout: PropTypes.string.isRequired,
    maxWidth: PropTypes.number,
    menu: PropTypes.bool.isRequired,
    redirectAfterSignIn: PropTypes.bool.isRequired,
    requireUser: PropTypes.bool.isRequired,
    schoolClass: PropTypes.instanceOf(SchoolClass),
    proTrialTopBar: PropTypes.bool,
    userMenu: PropTypes.bool.isRequired,
    weekBar: PropTypes.bool.isRequired
  })

  proTopBarHeight = 0
  windowWidth = new Animated.Value(Dimensions.get("window").width)
  windowHeight = new Animated.Value(Dimensions.get("window").height)

  setup() {
    setLayout(this.p.layout)

    const currentUser = useCurrentUser()
    const {showMenu} = useShowMenu()

    if (isExpo) {
      const {height, width} = useWindowDimensions()

      this.height = height
      this.width = width
    }

    useEventListener(Dimensions, "change", this.tt.onDimensionsChange)

    useMemo(() => {
      ApiMakerConfig.setBreakpoints(showMenu ? breakpointsWithExtraMenuSize : breakpointsWithoutExtraMenuSize)
    }, [showMenu])

    useEffect(() => {
      // This will be set instantly be useMemo instead

      return () => {
        ApiMakerConfig.setBreakpoints(breakpointsWithoutExtraMenuSize)
      }
    }, [])

    this.setInstance(useBreakpoint())
    this.setInstance({currentUser, showMenu})
    this.useStates({
      background: null,
      bottomBar: null,
      bottomBarHeight: undefined,
      actions: null,
      active: null,
      centered: null,
      className: null,
      contentCentered: null,
      darkBackground: false,
      headTitle: null,
      headerRight: null,
      headerTitle: null,
      headerTitleCentered: null,
      headerStyle: null,
      logo: true,
      menu: true,
      menuTriggered: false,
      requireUser: false,
      schoolClass: null,
      showUpgrade: false,
      subHeader: null,
      topLeft: null,
      topRight: null,
      proTrialTopBar: null,
      userMenu: true,
      weekBar: true
    })

    useMemo(() => {
      if (Platform.OS == "web") {
        this.setDocumentTitle()
      }
    }, [this.s.headTitle, this.s.headerTitle])

    useMemo(() => {
      this.setSchoolAndClass()
    }, [this.p.schoolClass?.id(), this.s.schoolClass?.id()])

    useEffect(() => {
      return () => {
        CommandsPool.current().globalRequestData.school_id = null
        CommandsPool.current().globalRequestData.school_class_id = null
      }
    }, [])
  }

  setSchoolAndClass() {
    CommandsPool.current().globalRequestData.school_id = this.p.schoolClass?.schoolId() || this.s.schoolClass?.schoolId()
    CommandsPool.current().globalRequestData.school_class_id = this.p.schoolClass?.id() || this.s.schoolClass?.id()
  }

  render() { // eslint-disable-line complexity
    const {currentUser, mdUp, showMenu, smUp, windowHeight, windowWidth, xlUp} = this.tt
    const {active, children, dataSet, maxWidth, requireUser} = this.p
    const {actions, bottomBar, bottomBarHeight, headerRight, menuTriggered, topLeft, topRight} = this.s
    const headerTitle = this.s.headerTitle || this.props.headerTitle
    const schoolClass = this.s.schoolClass || this.p.schoolClass

    let logo = true
    let menu = true
    let userMenu = true
    let weekBar = true

    if (!this.s.logo) logo = false
    if (!this.p.menu || !this.s.menu) menu = false
    if (!this.p.userMenu || !this.s.userMenu) userMenu = false
    if (!this.p.weekBar || !this.s.weekBar) weekBar = false

    const actualDataSet = useMemo(
      () => Object.assign(
        {component: "chalky-layout/image-background"},
        dataSet
      ),
      [dataSet]
    )

    let imageBackgroundStyle

    if (isExpo) {
      const {height, width} = this.tt

      imageBackgroundStyle = useMemo(() => ({width: "100%", height: "100%", minWidth: width, minHeight: height}), [width, height])
    } else {
      imageBackgroundStyle = styles.imageBackground
    }

    let centered = false
    let showSignIn = false
    let proTrialTopBar = true
    let actualShowMenu, logoAlign, logoHeight, logoWidth

    if (this.p.centered === false || this.s.centered === false) {
      centered = false
    } else if (this.p.centered || this.s.centered) {
      centered = true
    }

    if (this.p.proTrialTopBar === false || this.s.proTrialTopBar === false) proTrialTopBar = false
    if (requireUser && !currentUser) showSignIn = true

    if (menuTriggered) {
      actualShowMenu = true
    } else if (!currentUser || !menu || !showMenu) {
      actualShowMenu = false
    } else {
      actualShowMenu = true
    }

    let paddingLeft

    if (menu && actualShowMenu) {
      if (xlUp) {
        paddingLeft = 310
      } else if (mdUp) {
        paddingLeft = 290
      } else {
        paddingLeft = 220
      }
    }

    if (showSignIn) {
      logoAlign = "middle"
      logoWidth = 143
      logoHeight = 60
    } else if (mdUp) {
      logoAlign = "topLeft"
      logoWidth = 165
      logoHeight = 57
    } else {
      logoAlign = "middle"
      logoWidth = 116
      logoHeight = 40
    }

    return (
      <WithDefaultStyle style={defaultStyle}>
        <ImageBackground
          dataSet={actualDataSet}
          resizeMode="cover"
          source={this.s.background || background}
          style={imageBackgroundStyle}
        >
          <ClickOutsideView style={this.clickOutsideViewStyle ||= {width: "100%", height: "100%"}} testID="clickOutsideView">
            {proTrialTopBar &&
              <ProTopBar onLayout={this.tt.onProTopBarLayout} onUpgradePressed={this.tt.onUpgradePressed} />
            }
            {this.s.showUpgrade &&
              <ProUpgradeOverlay onRequestClose={this.tt.onRequestClosePlansSignUpModal} schoolClass={schoolClass} />
            }
            {bottomBar &&
              <View
                dataSet={this.bottomBarContainerDataSet ||= {class: "bottom-bar-container"}}
                onLayout={this.tt.onBottomBarContainerLayout}
                style={this.bottomBarContainerStyle ||= {
                  position: "fixed",
                  bottom: 0,
                  zIndex: 9,
                  width: "100%",
                  backgroundColor: "rgba(1, 1, 1, 0.5)",
                  padding: 20
                }}
              >
                <WithDefaultStyle style={this.bottomBarContainerDefaultStyle ||= {Text: {color: "#000"}}}>
                  {bottomBar}
                </WithDefaultStyle>
              </View>
            }
            <Animated.View
              dataSet={this.forceSizeContainerDataSet ||= {class: "force-size-container"}}
              style={this.cache("forceSizeContainerStyle", {
                width: "100%",
                height: "100%",
                minWidth: windowWidth,
                minHeight: windowHeight,
                alignItems: centered ? "center" : undefined,
                justifyContent: centered ? "center" : undefined,
                paddingLeft,
                paddingBottom: bottomBarHeight
              }, [centered, paddingLeft, bottomBarHeight])}
            >
              {currentUser && menu && !showMenu &&
                <Pressable onPress={this.tt.onMenuButtonPress} style={this.menuButtonStyle ||= {position: "absolute", zIndex: 9, top: 20, left: 20}}>
                  <Icon name="bars" size={24} />
                </Pressable>
              }
              {actualShowMenu &&
                <Menu
                  active={active || this.s.active}
                  onRequestClose={this.tt.onMenuRequestClose}
                  onUpgradePressed={this.tt.onUpgradePressed}
                  showMenu={showMenu}
                />
              }
              {logo && (!menu || !actualShowMenu) &&
                <View
                  dataSet={this.logoContainerDataSet ||= {class: "chalky-layout-logo-container"}}
                  style={this.cache("logoContainerStyle", {
                    position: (mdUp && logoAlign != "middle") ? "absolute" : undefined,
                    top: mdUp ? 80 : undefined,
                    marginTop: !mdUp ? 40 : undefined,
                    width: "100%",
                    maxWidth
                  }, [logoAlign, mdUp])}
                >
                  <Image
                    dataSet={this.logoDataSet ||= {class: "chalky-layout-logo"}}
                    resizeMode="contain"
                    source={LogoImage}
                    style={this.cache("logoStyle", {
                      position: logoAlign == "topLeft" ? "absolute" : undefined,
                      top: logoAlign == "topLeft" ? 0 : undefined,
                      left: logoAlign == "topLeft" ? 33 : undefined,
                      width: logoWidth,
                      height: logoHeight,
                      marginHorizontal: logoAlign == "middle" ? "auto" : undefined,
                      marginBottom: logoAlign == "middle" ? 48 : undefined
                    }, [logoAlign, logoHeight, logoWidth, mdUp])}
                  />
                </View>
              }
              <View
                dataSet={this.chalkyLayoutContainerDataSet ||= {class: "chalky-layout-container"}}
                style={this.cache("layoutContainerStyle", {
                  padding: mdUp ? 40 : 10,
                  width: "100%",
                  maxWidth
                }, [maxWidth, mdUp])}
              >
                {currentUser && (actions || topLeft || topRight || userMenu || weekBar) &&
                  <View
                    style={this.cache("userBarStyle", {
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: mdUp ? "end" : "center",
                      marginTop: mdUp ? 20 : 50
                    }, [mdUp])}
                  >
                    {topLeft &&
                      <View style={this.topLeftContainerStyle ||= {marginRight: "auto"}}>
                        {topLeft}
                      </View>
                    }
                    {actions &&
                      <View style={this.actionsContainerStyle ||= {marginRight: 15}}>
                        {actions}
                      </View>
                    }
                    {topRight &&
                      <View style={this.topRightContainerStyle ||= {marginRight: 15}}>
                        {topRight}
                      </View>
                    }
                    {weekBar &&
                      <WeekBar />
                    }
                    {userMenu &&
                      <>
                        <View style={this.userIconStyle ||= {marginLeft: 16}}>
                          <Icon color="#2c2c96" name="circle-user" size={20} version="FontAwesome6" />
                        </View>
                        <Link to={Routes.userProfilePath()}>
                          <Text
                            dataSet={this.userTextDataSet ||= {class: "user-name-text"}}
                            style={this.userTextStyle ||= {marginLeft: 8, color: "#2c2c96", fontSize: 16, fontWeight: 700}}
                          >
                            {(() => {
                              if (mdUp) {
                                return currentUser.name()
                              } else if (smUp) {
                                return currentUser.name().split(/\s+/)[0]
                              } else {
                                return ""
                              }
                            })()}
                          </Text>
                        </Link>
                      </>
                    }
                  </View>
                }
                <View
                  dataSet={this.contentContainerDataSet ||= {class: "content-container"}}
                  style={this.contentContainerStyle ||= {
                    marginTop: 38
                  }}
                >
                  {showSignIn &&
                    <View style={this.signInContainerStyle ||= {alignItems: "center", justifyContent: "center"}}>
                      <SignIn redirectAfterSignIn={this.p.redirectAfterSignIn} />
                    </View>
                  }
                  {!showSignIn &&
                    <>
                      {(headerTitle || headerRight) &&
                        <View style={{flexDirection: "row", alignItems: "center"}}>
                          {headerTitle &&
                            <Text style={this.headerTitleStyle ||= {fontFamily: "DreamChaser", fontSize: 48}} testID="header-title">
                              {headerTitle}
                            </Text>
                          }
                          {headerRight &&
                            <View style={{marginLeft: "auto"}}>
                              {headerRight}
                            </View>
                          }
                        </View>
                      }
                      <FrontLayoutContext.Provider value={this.tt.frontLayoutContext}>
                        {children}
                      </FrontLayoutContext.Provider>
                    </>
                  }
                </View>
              </View>
            </Animated.View>
          </ClickOutsideView>
        </ImageBackground>
      </WithDefaultStyle>
    )
  }

  frontLayoutContext = {
    setState: (states) => this.setState(states)
  }

  onBottomBarContainerLayout = (e) => this.setState({bottomBarHeight: e.nativeEvent.layout.height})

  onDimensionsChange = ({window}) => {
    this.windowWidth.setValue(window.width)
    this.windowHeight.setValue(window.height - this.tt.proTopBarHeight)
  }

  onMenuButtonPress = () => this.setState({menuTriggered: !this.s.menuTriggered})
  onMenuRequestClose = () => this.setState({menuTriggered: false})
  onRequestClosePlansSignUpModal = () => this.setState({showUpgrade: false})

  onProTopBarLayout = (e) => {
    this.proTopBarHeight = digg(e, "nativeEvent", "layout", "height")
    this.tt.windowHeight.setValue(Dimensions.get("window").height - this.tt.proTopBarHeight)
  }

  onUpgradePressed = () => {
    if (isUserA(this.tt.currentUser, "tester")) {
      this.setState({showUpgrade: true})
    } else {
      let redirectUrl

      if (I18n.locale == "da") {
        redirectUrl = "https://planetwoof.io/da/priser/"
      } else if (I18n.locale == "no") {
        redirectUrl = "https://planetwoof.io/no/priser/"
      } else if (I18n.locale == "se") {
        redirectUrl = "https://planetwoof.io/sv/priser/"
      } else {
        redirectUrl = "https://planetwoof.io/us/plans-pricing/"
      }

      window.open(redirectUrl, "_blank")
    }
  }

  setDocumentTitle() {
    const headTitle = this.s.headTitle || this.s.headerTitle

    if (headTitle) {
      document.title = headTitle
    } else {
      document.title = "WOOF"
    }
  }
}))
